import React, { useEffect, useState } from 'react'
import styles from 'styles/components/AccountTypeForm.module.sass'
import SecondaryButton from 'components/common/SecondaryButton'
import PrimaryButton from 'components/common/PrimaryButton'
import AccountType from 'interface/AccountType'
import ToggleSwitch from 'components/common/ToggleSwitch'
import { useToast } from 'provider/ToastProvider'
import { useAuth } from 'hooks/AuthContext'
import { useLocation, useNavigate } from 'react-router-dom'
import ZeroneLoader from 'components/common/ZeroneLoader'
import { usePlanStatus } from 'provider/PlanStatusProvider'


/**
 * ChangePlanForm component allows users to change their subscription plan.
 * It provides options for different account types and billing cycles.
 * 
 * @component
 * 
 * @example
 * // Renders the ChangePlanForm component
 * <ChangePlanForm />
 * 
 * @see PrimaryButton - Button component for the "Next" action
 * @see SecondaryButton - Button component for the "Update later" action
 * 
 * @description
 * This form presents users with different account types to choose from:
 * - Free: Basic access to blogs and trending reports
 * - Basic and Sapphire: Mid-tier options with additional features
 * - Gold and Platinum: Premium options with full access to services
 * 
 * Users select an account type via radio buttons. The form includes a "Next" button to proceed
 * with the selected account type and a "Update later" option for existing users.
 */
const ChangePlanForm: React.FC = () => {
    const [loading, setLoading] = useState(false)
    const [message, setMessage] = useState('')
    //const [accountType, setAccountType] = useState<AccountType | null>(null)
    const [selectedAccount, setSelectedAccount] = useState<'free' | 'basic' | 'sapphire' | 'gold' | 'platinum' | 'sandbox'>('basic')
    const [selectedBillingCycle, setSelectedBillingCycle] = useState<'monthly' | 'annual'>('monthly')
    // const [paymentRequired, setPaymentRequired] = useState(false)
    const billingOptions = ['monthly', 'annual']
    const accountTypes = usePlanStatus().activeAccountTypes;

    const { addToast } = useToast()
    const { user, userSubscription } = useAuth()
    const location = useLocation()
    const { package: newPackage } = (location.state || {}) as { package?: string }

    const navigate = useNavigate()

    useEffect(() => {
        if (!user || !userSubscription) {
            addToast({
                type: 'error',
                message: 'You must be logged in to change your plan.',
            })
            navigate('/login')
            return
        }

        // Pilot participants can't change plans during their 4-week window
        // (also enforced server-side in applySubscriptionChange) - without
        // this check, the accountTypes.find() below falls through to the
        // generic "error loading account" message, since Pilot is
        // deliberately absent from the public plan list accountTypes reads.
        if (userSubscription.name.toLowerCase() === 'pilot') {
            addToast({
                type: 'info',
                message: 'Subscription changes aren\'t available during your Pilot period.',
            })
            navigate('/profile')
            return
        }

        const currentAccountType = accountTypes.find((type) => type.name === userSubscription.name)
        if (!currentAccountType) {
            addToast({
                type: 'error',
                message: 'We encountered an error while loading your account. Please try again later.',
            })
            navigate('/')
            return
        }

        setMessage('')
        //setAccountType(currentAccountType)
        setSelectedAccount(currentAccountType.name.toLowerCase() as 'free' | 'basic' | 'sapphire' | 'gold' | 'platinum' | 'sandbox')
        setSelectedBillingCycle(userSubscription.billingCycle)
    }, [user, userSubscription, newPackage])

    // If a package is passed in the state, set it as the selected account
    useEffect(() => {
        if (newPackage && accountTypes.some(type => type.name.toLowerCase() === newPackage.toLowerCase())) {
            setSelectedAccount(newPackage.toLowerCase() as typeof selectedAccount);
        }
    }, [newPackage]);

    const handleBillingclicked = (option: string) => {
        setSelectedBillingCycle(option as 'monthly' | 'annual')
    }

    if (!user || !userSubscription ) {
        return (
            <div className={styles['account-form']}>
                <h2>Change Plan</h2>
                <p>You must be logged in to change your plan.</p>
            </div>
        )
    }

    const isSamePlan = () => {
        return selectedAccount === userSubscription?.name.toLowerCase() && selectedBillingCycle === userSubscription?.billingCycle;
    }

    const validSelection = (): boolean => {
        if (!selectedAccount || !['free', 'basic', 'sapphire', 'gold', 'platinum', 'sandbox'].includes(selectedAccount)) {
            addToast({
                type: 'error',
                message: 'Please select a valid account type.',
            })
            return false
        }

        if (selectedBillingCycle !== 'monthly' && selectedBillingCycle !== 'annual') {
            addToast({
                type: 'error',
                message: 'Please select a valid billing cycle.',
            })
            return false
        }

        if (isSamePlan()) {
            addToast({
                type: 'info',
                message: 'You are already on the selected plan.',
            })
            return false
        }
        return true
    }


    function handleNextClicked(event: React.FormEvent<Element>): void {
        event.preventDefault()

        try {
            if (!validSelection()) {
                return
            }

            navigate('/change-plan/invoice', { 
                state: {
                    accountType: selectedAccount,
                    billingCycle: selectedBillingCycle,
                }
            })
        } finally {
            setLoading(false)
        }
    }

    return (
        <form className={styles['account-form']}>
            <h2 className={styles['form-title']}>Choose Account Type</h2>
            <div className={styles['billing-cycle-container']}>
                <p className={styles['billing-note']}>
                    All plans are <span className={styles['highlight']}>50% off now!</span> Workspace is coming soon - grab the discount before it launches at full price!
                </p>
                <ToggleSwitch
                    selectedOption={selectedBillingCycle}
                    options={billingOptions}
                    onOptionChange={handleBillingclicked}
                />
            </div>
            {/* Account Type Options */}
            <div className={styles['account-options']}>
                {accountTypes.map((account: AccountType, index: number) => (
                    <div
                        key={index}
                        className={`${styles['account-option']} ${selectedAccount === account.name ? styles['selected'] : ''}`}
                        onClick={() => setSelectedAccount(account.name)}
                    >
                        <label className={styles['radio-label']}>
                            <input
                                type="radio"
                                name="accountType"
                                value={account.name}
                                checked={selectedAccount === account.name.toLocaleLowerCase()}
                                onChange={() => setSelectedAccount(account.name.toLowerCase() as typeof selectedAccount)}
                                className={styles['radio-input']}
                            />
                            <span
                                className={styles['radio-text']}
                                style={{ color: account.color }}
                            >
                                {account.name}
                            </span>
                        </label>
                        <p className={styles['account-description']}>{account.description}</p>
                    </div>
                ))}
            </div>

            {/* Buttons */}
            <div className={styles['action-buttons']}>
                <PrimaryButton
                    text='Next'
                    width='auto'
                    primaryColor='#FF5522'
                    textColor='#FFFFFF'
                    hoverColor='#FFFFFF'
                    hoverTextColor='#FF5522'
                    doSomething={(event: React.FormEvent) => handleNextClicked(event)}
                />
                <SecondaryButton
                    text="Update later"
                    width='auto'
                    primaryColor="transparent"
                    textColor="#ff5522"
                    hoverColor="#ff5522"
                    hoverTextColor="#FFFFFF"
                    doSomething={() => navigate('/')}
                />
            </div>
            {loading && (
                <div className={styles['loader']}>
                    <ZeroneLoader text={message} />
                </div>
            )}
        </form>
    )
}

export default ChangePlanForm;